// React-Query hooks for the multi-tenant signup/onboarding flow.
// `useCurrentTenant` is the gating query: a non-null result means the
// user has completed onboarding; null means redirect to /welcome.
// `useCreateTenant` is the wizard's submit mutation.

import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDataService } from '@/lib/service-context';
import { useAuth } from '@/lib/auth-context';
import { useNativeBridge } from '@/lib/native-context';
import { readBootHintSync, writeBootHint } from '@/lib/boot-hint';
import type { CreateTenantInput } from '@erp/data-access/interfaces';
import { TRIAL_DURATION_DAYS } from '@erp/domain/billing';

export const CURRENT_TENANT_KEY = ['current-tenant'] as const;
const TENANT_BOOT_TIMEOUT_MS = 7000;

export function useCurrentTenant() {
  const ds = useDataService();
  const { user } = useAuth();
  const bridge = useNativeBridge();
  const query = useQuery({
    queryKey: CURRENT_TENANT_KEY,
    queryFn: () => withTenantBootTimeout(ds.tenants.getCurrent()),
    // Tenancy almost never changes during a session — bump the staleTime
    // so we don't refetch on every navigation. We invalidate explicitly
    // after onboarding completes (see useCreateTenant below).
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    retry: false,
    networkMode: 'always',
  });

  // Persist the (user, tenant) pair to the boot hint so the *next* cold
  // launch can render the right route immediately, before any network
  // round-trip resolves. This is what makes the second-launch splash
  // disappear in milliseconds instead of ~1s.
  //
  // CRITICAL: only write `no_tenant` when we have a confirmed-empty
  // SUCCESSFUL response. A failed/errored fetch (offline) leaves
  // `query.data` undefined too, but it must NEVER overwrite a previously
  // good `ready` hint — otherwise the next cold launch (or the very
  // next ProtectedRoute render) bounces a tenant-having user to
  // /welcome.
  useEffect(() => {
    if (!user) return;
    if (query.isLoading) return;
    if (query.data) {
      void writeBootHint(bridge.preferences, {
        kind: 'ready',
        userId: user.id,
        tenantId: query.data.id,
      });
    } else if (query.status === 'success') {
      // success + no data = server says this user has no tenant.
      //
      // BUT: never DOWNGRADE a previously-good `ready` hint for the same
      // user. Once a user has been observed having a tenant, the only
      // legitimate way to lose that hint is explicit logout (which writes
      // `logged_out`). A transient null from the server — caused by a
      // race with a session refresh, a Postgres RLS hiccup, or a stale
      // auth token mid-rotation — must not bounce the user back to the
      // onboarding wizard.
      const existing = readBootHintSync();
      if (existing.kind === 'ready' && existing.userId === user.id) return;
      void writeBootHint(bridge.preferences, { kind: 'no_tenant', userId: user.id });
    }
    // query.status === 'error' or 'pending' → leave the hint alone.
  }, [user, query.data, query.isLoading, query.status, bridge.preferences]);

  return query;
}

export function withTenantBootTimeout<T>(promise: Promise<T>): Promise<T> {
  if (typeof window === 'undefined') return promise;

  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error('tenant boot timeout'));
    }, TENANT_BOOT_TIMEOUT_MS);

    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        window.clearTimeout(timer);
        reject(err);
      },
    );
  });
}

export function useCreateTenant() {
  const ds = useDataService();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateTenantInput) => {
      const result = await ds.tenants.create(input);
      // Seed the trial subscription right after the tenant is created.
      // Idempotent on the server — no-op if a row already exists. Failure
      // here is non-fatal: the gate treats a missing subscription row as
      // "fail open" (computeAccess returns 'full'), so the user still gets
      // into the app and the next page load will retry the seed.
      try {
        await ds.subscriptions.ensureForCurrent(TRIAL_DURATION_DAYS);
      } catch {
        // Swallowed on purpose; see comment above.
      }
      return result;
    },
    onSuccess: async () => {
      // CRITICAL: refetch the current-tenant query *synchronously*
      // before the wizard navigates. Otherwise ProtectedRoute reads
      // the stale `null` cached value and bounces back to /welcome,
      // creating an "I just made my tenant but the app sent me back"
      // loop. We refetch first, then invalidate everything else.
      await qc.refetchQueries({ queryKey: CURRENT_TENANT_KEY });
      await qc.invalidateQueries();
    },
  });
}

export function useIsSubdomainTakenForSignup(subdomain: string) {
  const ds = useDataService();
  const v = subdomain.trim().toLowerCase();
  return useQuery({
    queryKey: ['signup-subdomain-taken', v] as const,
    queryFn: () => ds.tenants.isSubdomainTaken(v),
    enabled: v.length >= 3,
    staleTime: 30 * 1000,
  });
}
